import React from 'react';
import {Link} from 'react-router-dom';
import {useAppDispatch, useAppSelector} from '../store/hooks';
import {adminStatsApiSelector, adminStatsSelector} from '../store/selectors/admin.selectors';
import {fetchAdminStats} from '../store/slices/adminThunks';
import AdminStatTiles from './admin/AdminStatTiles';
import {ErrorState, LoadingState, Panel} from './admin/AdminPrimitives';
import {RefreshIcon} from './Icons';

/**
 * The admin overview.
 *
 * The stats fetch itself lives in `AdminRoute`, which mounts on every admin page — this container
 * only renders what that produced and offers a manual refresh. Nothing here polls: these are
 * counts an operator reads deliberately, and a number that changes under the cursor is worse than
 * one that is a minute old.
 */
const AdminDashboardContainer: React.FC = () => {
  const dispatch = useAppDispatch();
  const stats = useAppSelector(adminStatsSelector);
  const api = useAppSelector(adminStatsApiSelector);

  const refresh = () => {
    dispatch(fetchAdminStats());
  };

  if (!stats && api.status === 'pending') {
    return <LoadingState message="Loading the dashboard…" />;
  }

  if (!stats) {
    return (
      <ErrorState
        message={api.error ?? 'The dashboard could not be loaded.'}
        onRetry={refresh}
      />
    );
  }

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-end">
        <button
          type="button"
          onClick={refresh}
          disabled={api.status === 'pending'}
          className="inline-flex items-center gap-1.5 rounded-lg border border-gray-700 px-2.5 py-1
           text-[11px] font-medium text-gray-300 transition-colors hover:border-gray-600
           hover:bg-gray-800 disabled:opacity-40 cursor-pointer"
        >
          <RefreshIcon className={`h-3 w-3 ${api.status === 'pending' ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      <AdminStatTiles stats={stats} />

      <Panel title="Where to go next">
        <ul className="space-y-2 text-xs text-gray-400">
          <li>
            <Link to="/admin/signups?status=failed" className="text-indigo-400 hover:text-indigo-300">
              Failed signups
            </Link>{' '}
            — paid, no account. Resending the setup email from the user’s page is the fix once the
            account exists; before that, provisioning has to land first.
          </li>
          <li>
            <Link
              to="/admin/users?password_set=false"
              className="text-indigo-400 hover:text-indigo-300"
            >
              Accounts with no password
            </Link>{' '}
            — provisioned by checkout and still relying on the emailed login link.
          </li>
          <li>
            <Link
              to="/admin/users?sort=transcription_seconds_total&order=desc"
              className="text-indigo-400 hover:text-indigo-300"
            >
              Heaviest transcription users
            </Link>{' '}
            — lifetime billable seconds, highest first.
          </li>
        </ul>
      </Panel>
    </div>
  );
};

export default AdminDashboardContainer;
